import * as React from "react";
import { observer, inject } from "mobx-react";
import * as _ from "lodash";

import {Button, FormGroup, FormLabel, FormControl, Row, Col, Modal, Spinner} from "react-bootstrap";

import { TicketStore, ReportStore } from "../../stores";
import { Report } from "Stores/reportStore";
import {LabelGroup, LabelStore} from "Stores/labelStore";

import { ListSelector, SelectionItem } from "../Common/ListSelector";
import { SimpleCheckbox } from "Components/Common/SimpleCheckbox";
import {toJS} from "mobx";


export interface GenerationFormProps {
    ticketStore?: TicketStore
    reportStore?: ReportStore
    labelStore?: LabelStore
    currentReport?: any
    numberOfTix: number
    isModal?: boolean
    handleCloseModal?: (shouldRedirect?: boolean) => void
}

interface GenerationFormState {
    reportName?: string
    reportSummary?: string
    shouldIncludeScreenshots: boolean
    shouldIncludeComments: boolean
    sortingOptions: SelectionItem[]
    shouldLoadReportList?: boolean
}

@inject("reportStore", "ticketStore", "labelStore")
@observer
export class ReportGenerationForm extends React.Component<GenerationFormProps, GenerationFormState> {

    baseSortingOptions = [
        {id: "type", content: "Type", isSelected: false},
        {id: "course_name", content: "Course", isSelected: false},
        {id: "name", content: "Committee", isSelected: false},
        {id: "assignee_last_name", content: "Assignee", isSelected: false},
        {id: "status", content: "Status", isSelected: false},
        {id: "created_by_last_name", content: "ActionPlan Creator", isSelected: false},
        {id: "dashboard", content: "Dashboard Name", isSelected: false}
    ];

    state = {
        reportName: "",
        reportSummary: "",
        shouldIncludeScreenshots: true,
        shouldIncludeComments: true,
        sortingOptions: _.cloneDeep(this.baseSortingOptions),
        shouldLoadReportList: false
    };

    componentDidMount() {
        this.props.labelStore!.fetchLabelGroups().then(() => {
            const groups = toJS(this.props.labelStore!.labelGroups);
            if (groups.length > 0) {
                groups.forEach((element: LabelGroup) => {
                    const obj = {id: `label_${element.id.toString()}`, content: element.name, isSelected: false};
                    this.baseSortingOptions.push(obj);
                });
            }
            this.setState({ sortingOptions: _.cloneDeep(this.baseSortingOptions) });
        });
    }

    closeModal = () => {
        if (this.props.handleCloseModal) {
            this.props.handleCloseModal(this.state.shouldLoadReportList);
        }
    };

    clearForm = () => {
        this.setState({
            reportName: "",
            reportSummary: "",
            shouldIncludeScreenshots: false,
            shouldIncludeComments: false,
            sortingOptions: _.cloneDeep(this.baseSortingOptions)
        });
    };

    handleSortingSelection = (items: SelectionItem[], selected: SelectionItem[]) => {
        _.forEach(items, (s: SelectionItem) => {
            s.isSelected = false;
        });
        _.forEach(selected, (s: SelectionItem) => {
            s.isSelected = true;
        });

        this.setState({ sortingOptions: [...items, ...selected] });
    };

    handleIncludeCommentsChange = (next) => {
        this.setState({shouldIncludeComments: next});
    };

    handleIncludeScreenshotsChange = (next) => {
        this.setState({shouldIncludeScreenshots: next});
    };

    formIsValid = () => this.state.reportName !== "" && this.state.reportSummary !== "";

    saveReport = (event: React.FormEvent<HTMLFormElement>) => {
        const { reportName, reportSummary } = this.state;
        event.preventDefault();
        event.stopPropagation();

        if (!this.props.reportStore || reportName === undefined || reportSummary === undefined || !this.formIsValid()) return;

        const ordering = this.state.sortingOptions
            .filter((s: SelectionItem) => s.isSelected)
            .map((s: SelectionItem) => s.id);

        const report: Partial<Report> = {
            name: reportName,
            summary: reportSummary,
            include_comments: this.state.shouldIncludeComments,
            include_screenshots: this.state.shouldIncludeScreenshots,
            ordering: ordering ? ordering : []
        };

        this.props.reportStore.createReport(report).then(() => {
            this.setState({ shouldLoadReportList: true }, () => {
                this.closeModal();
                if (this.props.reportStore){
                    this.props.reportStore.clearSelectedTickets();
                }
            });
        });

    };

    renderForm() {
        if (!this.props.reportStore) return;

        const buttonStyle: React.CSSProperties = { marginLeft: 15 };

        return (
            <div className="report-form edit">
                <div className="header edit">
                    <span>{this.props.currentReport ? "Edit" : "Generate"} Report</span>
                    <div className="right">Total ActionPlans Selected: { this.props.numberOfTix }</div>
                </div>
                <div>
                    <form onSubmit={this.saveReport}>
                        <Row>
                            <Col md={5}>
                                <Row>
                                    <Col>
                                        <FormGroup controlId="report-name">
                                            <FormLabel>Report Name</FormLabel>
                                            <FormControl
                                                type="text"
                                                value={this.state.reportName}
                                                placeholder="Enter name"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    this.setState({reportName: e.currentTarget.value});
                                                }}/>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <FormGroup controlId="report-summary">
                                            <FormLabel>Summary Statement</FormLabel>
                                            <FormControl
                                                as="textarea"
                                                value={this.state.reportSummary}
                                                placeholder="Enter summary"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    this.setState({reportSummary: e.currentTarget.value});
                                                }}/>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                            <Col md={7}>
                                <Row>
                                    <Col>
                                        <FormGroup controlId="report-sorting" id="report-sorting">
                                            <FormLabel>Sort Information By</FormLabel>
                                            <ListSelector items={this.state.sortingOptions}
                                                          handleSelectionChange={this.handleSortingSelection}/>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <FormGroup id="report-options">
                                            <Row>
                                                <FormLabel>Report Options</FormLabel>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <SimpleCheckbox
                                                        isChecked={this.state.shouldIncludeScreenshots}
                                                        id="includeScreenshots"
                                                        label="Include Screenshots"
                                                        onChange={this.handleIncludeScreenshotsChange}/>
                                                </Col>
                                                <Col>
                                                    <SimpleCheckbox
                                                        isChecked={this.state.shouldIncludeComments}
                                                        id="includeComments"
                                                        label="Include Comments"
                                                        onChange={this.handleIncludeCommentsChange}/>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="text-end">
                                <Button variant="secondary" size="lg" id="report-form-clear" onClick={() => this.clearForm()}>
                                    Clear Form
                                </Button>
                                <Button variant="secondary" size="lg" id="report-form-cancel" onClick={this.closeModal} disabled={this.props.reportStore.isProcessing} style={buttonStyle}>
                                    Cancel
                                </Button>
                                <Button variant="primary" size="lg" id="report-form-save" type="submit" disabled={!this.formIsValid() || this.props.reportStore.isProcessing} style={ buttonStyle } >
                                    {this.props.reportStore.isProcessing ? <Spinner size={"sm"} variant={"success"} animation={"border"} className="green-spinner" /> : "Create Report"}
                                </Button>
                            </Col>
                        </Row>
                    </form>
                </div>
            </div>
        );
    }

    render() {
        if (!this.props.reportStore) return null;

        return (
            this.props.isModal ?
                <Modal show={true} size={"lg"} contentClassName="generate-report">
                    <Modal.Body>
                        {this.renderForm()}
                    </Modal.Body>
                </Modal> :
                this.renderForm()
        );
    }

}
